import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, CreditCard, Shield, Zap, Crown, ArrowRight, Loader2 } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const GRADIENT = `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`;

export const SubscriptionPage = () => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/my-subscription')
      ]);
      setPlans(plansRes.data);
      setCurrentSubscription(subRes.data);
    } catch (error) {
      // If subscription endpoint fails, just load plans
      try {
        const plansRes = await api.get('/subscription/plans');
        setPlans(plansRes.data);
      } catch (e) {
        toast.error('Erro ao carregar planos');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await api.post('/subscription/create-checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#22C55E]" />
      </div>
    );
  }

  // If user has active subscription, show status
  if (currentSubscription?.has_subscription) {
    const sub = currentSubscription.subscription;
    const plan = currentSubscription.plan;
    const expiresAt = new Date(sub.expires_at);
    
    return (
      <div className="min-h-screen bg-[#030303] py-12 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ background: `${PRIMARY_COLOR}20` }}>
              <Crown className="w-10 h-10 text-[#22C55E]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Assinatura Ativa</h1>
            <p className="text-zinc-400">Você tem acesso completo ao sistema</p>
          </div>

          <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] rounded-2xl p-8 border border-white/[0.04]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-zinc-400 text-sm">Plano atual</p>
                <p className="text-2xl font-bold text-white">{plan?.name}</p>
              </div>
              <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: `${PRIMARY_COLOR}20`, color: PRIMARY_COLOR }}>
                ATIVO
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Valor</span>
                <span className="text-white font-medium">{formatCurrency(sub.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Válido até</span>
                <span className="text-white font-medium">{expiresAt.toLocaleDateString('pt-BR')}</span>
              </div>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-12 text-black font-semibold"
              style={{ background: GRADIENT }}
            >
              Ir para o Dashboard
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl font-bold mb-4 bg-clip-text text-transparent"
            style={{ backgroundImage: GRADIENT }}
          >
            Escolha seu Plano
          </h1>
          <p className="text-zinc-400 text-lg">
            Acesso completo ao ALVO - Performance Empresarial
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {plans && Object.entries(plans).map(([planId, plan]) => (
            <div
              key={planId}
              className={`relative bg-gradient-to-br from-white/[0.03] to-white/[0.01] rounded-2xl p-8 border transition-all hover:border-[#22C55E]/30 ${
                planId === 'anual' ? 'border-[#22C55E]/50' : 'border-white/[0.04]'
              }`}
            >
              {/* Popular badge */}
              {planId === 'anual' && (
                <div 
                  className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-black"
                  style={{ background: GRADIENT }}
                >
                  MAIS POPULAR
                </div>
              )}

              {/* Plan header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{plan.name}</h2>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold" style={{ color: PRIMARY_COLOR }}>
                    {formatCurrency(plan.price)}
                  </span>
                  <span className="text-zinc-500">
                    /{plan.interval === 'month' ? 'mês' : 'ano'}
                  </span>
                </div>
                {planId === 'anual' && (
                  <p className="text-[#22C55E] text-sm mt-2">
                    Economize R$ 267 (2 meses grátis)
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={feature} className="flex items-center gap-3 text-zinc-300">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${PRIMARY_COLOR}20` }}>
                      <Check className="w-3 h-3" style={{ color: PRIMARY_COLOR }} />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={() => handleSubscribe(planId)}
                disabled={processingPlan !== null}
                className={`w-full h-12 font-semibold transition-all ${
                  planId === 'anual' ? 'text-black' : 'text-white bg-white/10 hover:bg-white/20'
                }`}
                style={planId === 'anual' ? { background: GRADIENT } : {}}
              >
                {processingPlan === planId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Assinar Agora
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="mt-12 flex items-center justify-center gap-8 text-zinc-500 text-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Pagamento seguro
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Acesso imediato
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Cartão ou PIX
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
